import type {
	DexfSettingsEnvironmentEnum,
	DexfSettingsFilterSegmentEnum
} from '@inter-ikea-kompis/enums';
import { ServiceException } from '@inter-ikea-kompis/exceptions';
import type { IDexfSettings } from '@inter-ikea-kompis/types';

import DexfSettingsService from '../../services/localization/DexfSettingsService.js';
import type ISettingsServiceOptions from './ISettingsServiceOptions.js';

/**
 * This class is used as a simple interface for settings configuration services.
 */
export default class SettingsService {
	private readonly dexfApiKey: string;
	private readonly dexfEnvironment: DexfSettingsEnvironmentEnum | undefined;
	private readonly dexfApplicationId: string;
	private readonly locale: string;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.dexfApiKey ...
	 * @param options.dexfApplicationId ...
	 * @param options.locale ...
	 */
	constructor(options: ISettingsServiceOptions) {
		this.dexfApiKey = options.dexfApiKey;
		this.dexfEnvironment = options.dexfEnvironment;
		this.dexfApplicationId = options.dexfApplicationId;
		this.locale = options.locale;
	}

	/**
	 * Fetches and returns configuration settings from DEXF.
	 *
	 * @param filterSegment ...
	 * @returns Promise object.
	 */
	public async getApplicationSettings(
		filterSegment?: DexfSettingsFilterSegmentEnum
	): Promise<IDexfSettings> {
		if (!this.dexfApiKey) {
			throw new ServiceException(
				'Failed to fetch application settings. The configuration "dexfApiKey" has not been defined. Send it in to the constructor of SettingsService.'
			);
		}
		if (!this.dexfApplicationId) {
			throw new ServiceException(
				'Failed to fetch application settings. The configuration "dexfApplicationId" has not been defined. Send it in to the constructor of SettingsService.'
			);
		}
		if (!this.locale) {
			throw new ServiceException(
				'Failed to fetch application settings. The configuration "locale" has not been defined. Send it in to the constructor of SettingsService.'
			);
		}

		const service = new DexfSettingsService({
			dexfApiKey: this.dexfApiKey,
			environment: this.dexfEnvironment
		});
		const configurationSettings = await service.getApplicationSettings(
			this.dexfApplicationId,
			this.locale,
			filterSegment
		);

		return configurationSettings;
	}
}
